import React, {
  useState,
  type Dispatch,
  type SetStateAction,
  useMemo,
  useEffect,
} from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  Typography,
  IconButton,
  Collapse,
  Chip,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { IUser } from '../../../Interfaces/IUser';
import { IRole } from '../../../Interfaces/IRole';

interface ClientsSearchProps {
  users: IUser[];
  setFilteredUsers: Dispatch<SetStateAction<IUser[]>>;
  roles: IRole[];
}

export const UserSearch: React.FC<ClientsSearchProps> = ({
  users,
  setFilteredUsers,
  roles,
}) => {
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 270,
        width: 300,
      },
    },
  };
  const MenuPropsSmall = {
    PaperProps: {
      style: {
        maxHeight: 270,
        width: 175,
      },
    },
  };
  const searchOptions = [
    { value: 'idUser', label: 'Nr.' },
    { value: 'Login', label: 'Benutzername' },
    { value: 'FirstName', label: 'Vorname' },
    { value: 'LastName', label: 'Nachname' },
  ];

  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchFilter, setSearchFilter] = useState<string[]>(
    searchOptions.map(o => o.value)
  );
  const [filterAll, setFilterAll] = useState(true);

  const [activ, setActiv] = useState<'ALL' | 'TRUE' | 'FALSE'>('ALL');
  const [notar, setNotar] = useState<'ALL' | 'TRUE' | 'FALSE'>('ALL');
  const [roleFilter, setRoleFilter] = useState<number | 'ALL'>('ALL');

  const filterUsers = useMemo(() => {
    if (!users) return [];

    return users.filter(user => {
      // --- Normale Suchfilterung ---
      let matchesSearch = true;

      if (searchValue && searchFilter.length > 0) {
        const val = searchValue.toLowerCase();

        matchesSearch = searchFilter.some(filter => {
          switch (filter) {
            case 'idUser':
              return String(user.idUser).includes(val);
            case 'Login':
              return user.Login.toLowerCase().includes(val) ?? false;
            case 'FirstName':
              return user.FirstName.toLowerCase().includes(val) ?? false;
            case 'LastName':
              return user.LastName?.toLowerCase().includes(val) ?? false;
            default:
              return false;
          }
        });
      }

      // --- Erweiterte Filter ---
      let matchesExtended = true;

      if (activ === 'TRUE') {
        matchesExtended = matchesExtended && !user.isBlocked;
      } else if (activ === 'FALSE') {
        matchesExtended = matchesExtended && user.isBlocked;
      }

      if (notar === 'TRUE') {
        matchesExtended = matchesExtended && Boolean(user.isNotary);
      } else if (notar === 'FALSE') {
        matchesExtended = matchesExtended && !user.isNotary;
      }

      if (roleFilter !== 'ALL') {
        matchesExtended = matchesExtended && user.idRole === roleFilter;
      }

      return matchesSearch && matchesExtended;
    });
  }, [users, searchFilter, searchValue, activ, notar, roleFilter]);

  const activeFilterChips = useMemo(() => {
    // wenn alle ausgewählt sind -> alle Chips anzeigen
    if (searchFilter.length === searchOptions.length) {
      return searchOptions.map(opt => ({
        label: opt.label,
        onDelete: () => {
          const newFilters = searchFilter.filter(f => f !== opt.value);
          setSearchFilter(newFilters.length === 0 ? [] : newFilters);
          setFilterAll(newFilters.length === searchOptions.length);
        },
      }));
    }

    // sonst die aktiv gewählten als chip anzeigen
    return searchFilter.map(filterVal => {
      const option = searchOptions.find(opt => opt.value === filterVal);
      return {
        label: option?.label ?? filterVal,
        onDelete: () => {
          const newFilters = searchFilter.filter(f => f !== filterVal);
          setSearchFilter(newFilters.length === 0 ? [] : newFilters);
          setFilterAll(newFilters.length === searchOptions.length);
        },
      };
    });
  }, [searchFilter, searchOptions]);

  const activeChips = useMemo(() => {
    const chips: { label: string; onDelete: () => void }[] = [];

    if (roleFilter !== 'ALL') {
      const role = roles.find(r => r.idRole === roleFilter);
      if (role) {
        chips.push({
          label: `Rolle: ${role.Role}`,
          onDelete: () => setRoleFilter('ALL'),
        });
      }
    }

    if (activ === 'TRUE') {
      chips.push({
        label: 'Aktiv ✔',
        onDelete: () => setActiv('ALL'),
      });
    } else if (activ === 'FALSE') {
      chips.push({
        label: 'Aktiv ✖',
        onDelete: () => setActiv('ALL'),
      });
    }

    if (notar === 'TRUE') {
      chips.push({
        label: 'Notar ✔',
        onDelete: () => setNotar('ALL'),
      });
    } else if (notar === 'FALSE') {
      chips.push({
        label: 'Notar ✖',
        onDelete: () => setNotar('ALL'),
      });
    }

    return chips;
  }, [activ, notar, roleFilter]);

  useEffect(() => {
    setFilteredUsers(filterUsers);
  }, [filterUsers]);

  return (
    <>
      <Box mt={2} mb={2}>
        <TextField
          disabled={searchFilter.length === 0}
          fullWidth
          label='Suche'
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />

        <Box
          mt={0.5}
          ml={0.5}
          display='flex'
          alignItems='center'
          sx={{ cursor: 'pointer', width: 'fit-content' }}
          onClick={() => {
            setShowAdvancedSearch(prev => {
              const next = !prev;
              if (!next) {
                setSearchFilter(searchOptions.map(o => o.value));
                setFilterAll(true);
                setNotar('ALL');
                setActiv('ALL');
                setRoleFilter('ALL');
              }
              return next;
            });
          }}
        >
          <Typography variant='body2'>Erweiterte Suche</Typography>
          <IconButton size='small'>
            {showAdvancedSearch ? (
              <ExpandMore fontSize='small' />
            ) : (
              <ExpandLess fontSize='small' />
            )}
          </IconButton>
        </Box>

        <Collapse in={showAdvancedSearch} timeout='auto' unmountOnExit>
          <Box ml={0.5} mt={2} sx={{ display: 'flex', gap: 2, flexGrow: 1 }}>
            <FormControl variant='standard' sx={{ width: 300 }}>
              <InputLabel>Suche in</InputLabel>
              <Select
                multiple
                MenuProps={MenuProps}
                value={
                  searchFilter.length > 0 ? searchFilter : ['__no_filter__']
                }
                onChange={e => {
                  const val = (e.target.value as string[]).filter(
                    v => v !== '__no_filter__'
                  );
                  setSearchFilter(val);
                  setFilterAll(val.length === searchOptions.length);
                }}
                renderValue={(selected: string[]) => {
                  if (selected.includes('__no_filter__')) {
                    return 'keine Suchfilterung ausgewählt';
                  }
                  if (selected.length === searchOptions.length) {
                    return 'Alle Einträge';
                  }
                  return searchOptions
                    .filter(opt => selected.includes(opt.value))
                    .map(opt => opt.label)
                    .join(', ');
                }}
              >
                <Box mb={1}>
                  <Box ml={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={filterAll}
                          onChange={e => {
                            const checked = e.target.checked;
                            setFilterAll(checked);
                            setSearchFilter(
                              checked ? searchOptions.map(o => o.value) : []
                            );
                          }}
                        />
                      }
                      label='Alle Filter'
                    />
                  </Box>
                  <Divider variant='middle' />
                </Box>
                {searchOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant='standard' sx={{ minWidth: 200 }}>
              <InputLabel>Rolle</InputLabel>
              <Select
                MenuProps={MenuPropsSmall}
                value={roleFilter}
                onChange={e => {
                  const value = e.target.value;
                  setRoleFilter(value === 'ALL' ? 'ALL' : Number(value));
                }}
                renderValue={selected => {
                  if (selected === 'ALL') {
                    return 'Alle Einträge';
                  }
                  const role = roles.find(r => r.idRole === selected);
                  return role?.Role ?? '';
                }}
              >
                <MenuItem value='ALL'>Alle Einträge</MenuItem>
                {roles.map(role => (
                  <MenuItem key={role.idRole} value={role.idRole}>
                    {role.Role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl variant='standard' sx={{ minWidth: 175 }}>
              <InputLabel>Aktiv</InputLabel>
              <Select
                MenuProps={MenuPropsSmall}
                value={activ}
                onChange={e =>
                  setActiv(e.target.value as 'ALL' | 'TRUE' | 'FALSE')
                }
                renderValue={selected => {
                  switch (selected) {
                    case 'ALL':
                      return 'Alle Einträge';
                    case 'TRUE':
                      return '✔';
                    case 'FALSE':
                      return '✖';
                    default:
                      return '';
                  }
                }}
              >
                <MenuItem value='ALL'>Alle Einträge</MenuItem>
                <MenuItem value='TRUE'>✔</MenuItem>
                <MenuItem value='FALSE'>✖</MenuItem>
              </Select>
            </FormControl>

            <FormControl variant='standard' sx={{ minWidth: 175 }}>
              <InputLabel>Notar</InputLabel>
              <Select
                MenuProps={MenuPropsSmall}
                value={notar}
                onChange={e =>
                  setNotar(e.target.value as 'ALL' | 'TRUE' | 'FALSE')
                }
                renderValue={selected => {
                  switch (selected) {
                    case 'ALL':
                      return 'Alle Einträge';
                    case 'TRUE':
                      return '✔';
                    case 'FALSE':
                      return '✖';
                    default:
                      return '';
                  }
                }}
              >
                <MenuItem value='ALL'>Alle Einträge</MenuItem>
                <MenuItem value='TRUE'>✔</MenuItem>
                <MenuItem value='FALSE'>✖</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {activeFilterChips.length > 0 && (
            <Box mt={1}>
              <Typography variant='overline' gutterBottom>
                Suche in
              </Typography>
              <Box display='flex' gap={1} flexWrap='wrap'>
                {activeFilterChips.map((chip, idx) => (
                  <Chip
                    key={`filter-${idx}`}
                    label={chip.label}
                    onDelete={chip.onDelete}
                    color='default'
                    variant='outlined'
                  />
                ))}
              </Box>
            </Box>
          )}

          {activeChips.length > 0 && (
            <Box mt={1}>
              <Typography variant='overline' gutterBottom>
                Weitere Filter
              </Typography>

              <Box display='flex' gap={1} flexWrap='wrap'>
                {activeChips.map((chip, idx) => (
                  <Chip
                    key={`state-${idx}`}
                    label={chip.label}
                    onDelete={chip.onDelete}
                    color='default'
                    variant='outlined'
                  />
                ))}
              </Box>
            </Box>
          )}
        </Collapse>
      </Box>
    </>
  );
};
